import { useState, useEffect } from 'react';
import { Download, FileArchive, Clock, CheckCircle, Package, ExternalLink } from 'lucide-react';
import { synapseAPI } from '../lib/synapse-api';

interface Task {
  task_id: string;
  description: string;
  status: string;
  phase: string;
  result?: {
    message?: string;
  };
  created_at: string;
  completed_at?: string;
}

export function ExportsPage() {
  const [deployedTasks, setDeployedTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDeployedTasks();
  }, []);

  const loadDeployedTasks = async () => {
    setLoading(true);
    setError(null);

    try {
      const tasks = await synapseAPI.getTasks(50);

      // Filter to completed tasks only
      const completed = tasks.filter((t: Task) =>
        t.status === 'COMPLETED' || t.status === 'COMPLETE'
      );

      setDeployedTasks(completed);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setError('Failed to connect to Synapse. Make sure it\'s running.');
    } finally {
      setLoading(false);
    }
  };

  const extractWorkspacePath = (task: Task): string | null => {
    const message = task.result?.message || '';
    const match = message.match(/Workspace: (.+)/);
    return match ? match[1] : null;
  };

  const handleDownload = async (task: Task) => {
    const workspacePath = extractWorkspacePath(task);

    if (!workspacePath) {
      alert('No workspace path found for this task');
      return;
    }

    // For now, show the path - in the future this would trigger a download
    alert(`Task workspace location:\n${workspacePath}\n\nDownload functionality coming soon!`);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] text-white p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400">Loading exports...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Exports</h1>
          <p className="text-gray-400">Download backups, exported projects, and completed tasks</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-400">
              <span className="text-xl">⚠️</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Deployed Tasks Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-5 h-5 text-blue-400" />
            <h2 className="text-xl font-semibold">Deployed Tasks ({deployedTasks.length})</h2>
          </div>

          {deployedTasks.length === 0 ? (
            <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-8 text-center text-gray-400">
              No completed tasks yet
            </div>
          ) : (
            <div className="space-y-3">
              {deployedTasks.map((task) => {
                const workspacePath = extractWorkspacePath(task);
                const hasOutput = !!workspacePath;

                return (
                  <div
                    key={task.task_id}
                    className="bg-[#1a1a1a] rounded-lg border border-gray-800 p-4 hover:border-gray-700 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <h3 className="font-medium truncate">{task.description}</h3>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="font-mono text-xs">{task.task_id.slice(0, 8)}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(task.created_at)}
                          </span>
                          {hasOutput && (
                            <span className="text-blue-400">Output available</span>
                          )}
                        </div>

                        {workspacePath && (
                          <div className="mt-2 text-xs text-gray-500 font-mono bg-black/30 p-2 rounded">
                            {workspacePath}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {hasOutput ? (
                          <button
                            onClick={() => handleDownload(task)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            View
                          </button>
                        ) : (
                          <span className="px-4 py-2 bg-gray-800 rounded-lg text-sm text-gray-500">
                            N/A
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="flex gap-3">
            <ExternalLink className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-medium text-white mb-1">Export System Status</p>
              <p>Connected to Synapse on port 8765. Task outputs are stored in <code className="bg-black/30 px-1 rounded">/tmp/poli-task-*</code> directories.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
